import axios from 'axios';
import logger from '../utils/logger';
import toast from 'react-hot-toast';
import rateLimiter from '../utils/rateLimiter';
import environmentConfig from '../utils/environmentConfig';
import { canAutoLoginReload, recordAutoLoginReload } from '../utils/autoLoginGuard';
import { hasOfficerndAutoLoginParams } from '../utils/officerndAutoLoginParams';

// Get API URL from environment configuration
let API_BASE_URL = environmentConfig.apiUrl;

// Timeout toast debounce — show at most 1 timeout toast per 15 seconds
let lastTimeoutToast = 0;

// Log the environment and API URL being used
logger.info('API', 'Environment configuration loaded', {
  environment: environmentConfig.environment,
  apiUrl: API_BASE_URL,
  isProduction: environmentConfig.isProduction(),
  isLocal: environmentConfig.isLocal()
});

logger.info('API', 'API Service initialized', { baseURL: API_BASE_URL });

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // 60 second timeout
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false, // Backend uses JSON tokens (localStorage), not cookies
  maxBodyLength: 100 * 1024 * 1024, // 100MB max request body (for video uploads)
  maxContentLength: 100 * 1024 * 1024, // 100MB max response size
});

// Add retry logic for rate limits
api.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;
    
    // If rate limited (429) and haven't retried yet
    if (error.response?.status === 429 && !originalRequest._retried) {
      originalRequest._retried = true;
      
      // Show user-friendly message
      toast.error('Too many requests. Please wait a moment...');
      logger.warn('API', 'Rate limit hit, retrying after delay', { url: originalRequest.url });
      
      // Wait 1 second before retrying
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      return api(originalRequest);
    }
    
    // If still rate limited after retry
    if (error.response?.status === 429) {
      toast.error('Server is busy. Please try again in a few seconds.');
      logger.error('API', 'Rate limit exceeded after retry', { url: originalRequest.url });
    }
    
    return Promise.reject(error);
  }
);

// Token management - JWT tokens stored in localStorage
// Backend returns tokens in JSON response body on login/refresh
let accessToken = localStorage.getItem('access_token');
let refreshToken = localStorage.getItem('refresh_token');

// Listen for token refresh events from TokenRefreshManager
if (typeof window !== 'undefined') {
  window.addEventListener('tokenRefreshed', (event) => {
    accessToken = event.detail.accessToken;
    logger.info('API', 'Token refreshed and updated');
  });
}

// Request interceptor to add auth token and rate limiting
api.interceptors.request.use(
  (config) => {
    // Client-side rate limiting check
    const endpoint = config.url.replace(API_BASE_URL, '');
    const rateLimit = rateLimiter.isAllowed(endpoint, config.method);
    
    if (!rateLimit.allowed) {
      logger.warn('API', 'Request rate limited', {
        endpoint,
        method: config.method,
        retryAfter: rateLimit.retryAfter
      });
      
      // Show user-friendly rate limit message
      toast.error(rateLimit.message);
      
      // Reject the request
      return Promise.reject(new Error(`Rate limit exceeded: ${rateLimit.message}`));
    }
    
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    
    // Log outgoing request (with rate limit info)
    // NOTE: Do NOT log data or headers - they may contain PII and auth tokens
    logger.debug('API', `Request: ${config.method?.toUpperCase()} ${config.url}`, {
      method: config.method,
      url: config.url,
      rateLimitRemaining: rateLimit.remaining
    });
    
    return config;
  },
  (error) => {
    // NOTE: Do NOT log full error object - it may contain PII
    logger.error('API', 'Request interceptor error', { message: error.message });
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => {
    // Log successful response
    // NOTE: Do NOT log response.data - it may contain PII
    logger.success('API', `Response: ${response.config.method?.toUpperCase()} ${response.config.url}`, {
      status: response.status,
      duration: response.config.metadata?.duration
    });
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Log error response with timeout detection
    const isTimeout = error.code === 'ECONNABORTED' || error.message.includes('timeout');
    const isCancelled = error.code === 'ERR_CANCELED' || error.message === 'canceled';
    const isLogSubmission = error.config?.url?.includes('/admin/logs/frontend');

    if (isCancelled) {
      // Request was intentionally cancelled (AbortController) — skip toast and logging
      return Promise.reject(error);
    }

    // CRITICAL: Skip ALL logging for log submission errors.
    // Logging a log-submission error adds to the batch → triggers another flush →
    // which fails → gets logged → infinite loop that floods the network
    // and blocks all other API requests with connection exhaustion.
    if (isLogSubmission) {
      return Promise.reject(error);
    }

    if (isTimeout) {
      logger.warn('API', `TIMEOUT: ${error.config?.method?.toUpperCase()} ${error.config?.url} exceeded ${error.config?.timeout}ms`);
      // Debounce: show at most 1 timeout toast per 15 seconds to prevent toast spam
      const now = Date.now();
      if (!error.config?.url?.includes('/upload') && now - lastTimeoutToast > 15000) {
        lastTimeoutToast = now;
        toast.error('Request timed out. The server is taking too long to respond.');
      }
    }

    // Log error details (response.data.detail is safe — it's a server error message, not PII)
    const isNetworkError = error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED';
    logger.error('API', `Error: ${error.config?.method?.toUpperCase()} ${error.config?.url}`, {
      status: error.response?.status,
      message: error.message,
      url: error.config?.url,
      code: error.code,
      detail: error.response?.data?.detail,
      isTimeout,
      isNetworkError
    });

    // Log extra context for network errors (often caused by CORS rejection)
    if (isNetworkError && !isTimeout) {
      logger.warn('API', `Network error on ${error.config?.url} — possible CORS rejection or server unreachable`, {
        baseURL: error.config?.baseURL,
        url: error.config?.url,
        hint: 'If this is an admin endpoint, verify the user has the correct role in the backend'
      });
    }

    if (error.response?.status === 401) {
      // Don't redirect on login endpoint failures - let the login component handle it
      if (originalRequest.url === '/auth/login' || originalRequest.url === '/auth/register') {
        return Promise.reject(error);
      }

      // Don't redirect if we have no tokens (user never successfully logged in)
      if (!accessToken && !refreshToken) {
        return Promise.reject(error);
      }

      // Don't attempt token refresh for log submission - it's non-critical
      // and retrying would create a feedback loop (log error → flush → 401 → retry → more errors)
      if (originalRequest.url?.includes('/admin/logs/frontend')) {
        return Promise.reject(error);
      }

      // Don't auto-logout for location endpoints - let the component handle the error
      if (originalRequest.url?.includes('/admin/locations')) {
        logger.warn('API', 'Location API authorization error - letting component handle it', { url: originalRequest.url });
        return Promise.reject(error);
      }

      // Don't auto-logout for admin user operations - let the component handle the error
      if (originalRequest.url?.includes('/admin/users')) {
        logger.warn('API', 'Admin user API authorization error - letting component handle it', { url: originalRequest.url });
        return Promise.reject(error);
      }
      
      // TokenRefreshManager handles all token refresh logic now
      // Just mark that we got a 401 and let TokenRefreshManager handle it
      if (!originalRequest._retry401) {
        originalRequest._retry401 = true;

        logger.info('API', '401 received - TokenRefreshManager will handle token refresh');

        // Import TokenRefreshManager dynamically to avoid circular dependency
        const { getTokenRefreshManager } = await import('./tokenRefreshManager');
        const refreshManager = getTokenRefreshManager();

        // Try to refresh the token
        const refreshResult = await refreshManager.refreshToken();

        if (refreshResult && refreshResult.access_token) {
          // Token was refreshed successfully
          accessToken = refreshResult.access_token;
          // Update refresh token if provided (rolling session)
          if (refreshResult.refresh_token) {
            refreshToken = refreshResult.refresh_token;
            logger.info('API', 'Updated refresh token for rolling session');
          }
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return api(originalRequest);
        }

        // Refresh failed — auto-login mode: reload page to re-trigger auto-login.
        // The OfficeRND params live in URL on first load, then in sessionStorage
        // after we scrub the URL on first checkAuth; either presence is enough
        // to recover the iframe session.
        if (hasOfficerndAutoLoginParams()) {
          if (canAutoLoginReload()) {
            recordAutoLoginReload();
            logger.info('API', 'Auto-login mode: reloading page to re-authenticate');
            window.location.reload();
            return new Promise(() => {}); // Never resolves — page is reloading
          } else {
            logger.error('API', 'Auto-login reload limit reached — stopping to prevent loop');
            return Promise.reject(error);
          }
        }

        // Normal mode: TokenRefreshManager will handle the logout if needed
        logger.warn('API', 'Token refresh failed - TokenRefreshManager will handle session expiry');
        return Promise.reject(error);
      } else {
        // Already tried once - don't retry
        logger.warn('API', '401 error after retry - request failed');
        return Promise.reject(error);
      }
    }

    // Handle 403 Forbidden - DO NOT auto-logout, it's just a permission issue
    // No global toast here — each page/component handles 403 with its own contextual message.
    if (error.response?.status === 403) {
      logger.warn('API', 'Permission denied (403)', {
        url: originalRequest.url,
        message: error.response?.data?.detail || 'Access forbidden'
      });

      // Let the component handle the error
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (email, password) => {
    const response = await api.post('/auth/login', {
      email,
      password,
    });
    // Only set tokens if they exist in the response
    if (response.data.access_token && response.data.refresh_token) {
      const { access_token, refresh_token } = response.data;
      accessToken = access_token;
      refreshToken = refresh_token;
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
    }
    return response.data;
  },

  register: async (userData, imageFile = null) => {
    let response;
    
    // NOTE: Do NOT log PII - email, names, phone
    logger.info('API', 'Registration request initiated', {
      hasImage: !!imageFile,
      hasPhone: !!userData.phone_number,
      hasEmail: !!userData.email
    });
    
    if (imageFile) {
      // Use FormData for multipart request
      const formData = new FormData();
      formData.append('email', userData.email);
      formData.append('password', userData.password);
      formData.append('first_name', userData.first_name || '');
      formData.append('last_name', userData.last_name || '');
      // Phone is required
      formData.append('phone_number', userData.phone_number);
      // SMS consent is required
      formData.append('sms_consent', userData.sms_consent || false);
      // Forwarded only on §2.4 auto-submit (SEND_LIMIT / 3 wrong codes) so admin
      // can see why Twilio failed. Omit on the happy path. Spec §11.
      if (userData.last_otp_error) {
        formData.append('last_otp_error', String(userData.last_otp_error).slice(0, 500));
      }
      // Receipt from /auth/verify-otp proving THIS browser passed the OTP check, so a phone
      // someone else verified cannot be claimed. Omitted when the user never verified — the
      // backend then falls back to its old check (flag REQUIRE_PHONE_VERIFICATION_TOKEN).
      if (userData.phone_verification_token) {
        formData.append('phone_verification_token', userData.phone_verification_token);
      }
      // Also append 'name' field for backend compatibility
      const fullName = `${userData.first_name || ''} ${userData.last_name || ''}`.trim();
      if (fullName) {
        formData.append('name', fullName);
      }
      formData.append('user_image', imageFile);
      
      response = await axios.post(`${API_BASE_URL}/auth/register`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    } else {
      // Use JSON for regular request
      // Add 'name' field for backend compatibility
      const registrationData = { ...userData };
      const fullName = `${userData.first_name || ''} ${userData.last_name || ''}`.trim();
      if (fullName) {
        registrationData.name = fullName;
      }
      response = await api.post('/auth/register', registrationData);
    }
    
    return response.data;
  },

  logout: async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      // Still logout locally even if API call fails (including rate limits)
      logger.warn('AUTH', 'Logout API call failed, logging out locally', { 
        error: error.response?.status === 429 ? 'Rate limited' : error.message 
      });
      
      // Don't show error for rate limits on logout - just proceed
      if (error.response?.status !== 429) {
        logger.debug('API', 'Logout error', {
          status: error.response?.status,
          message: error.message
        });
      }
    }
    
    // Always clear tokens and logout locally
    accessToken = null;
    refreshToken = null;
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  },

  getProfile: async () => {
    const response = await api.get('/auth/profile');
    return response.data;
  },

  /**
   * OfficeRND auto-login — called when Concierge is loaded inside OfficeRND iframe
   * with memberId, memberName, and token URL params.
   *
   * @param {string} memberId - OfficeRND member ID
   * @param {string} memberName - Member display name
   * @param {string} token - OfficeRND JWT token
   * @returns {Object} - { status, access_token, refresh_token, user }
   */
  officerndAutoLogin: async (memberId, memberName, token, clientNonce = '') => {
    logger.info('AUTH', 'OfficeRND auto-login initiated', {
      memberId,
      memberNamePresent: !!memberName,
      tokenLength: token?.length || 0,
      noncePresent: !!clientNonce,
    });

    // C9: send credentials in the POST body, never the URL query string —
    // a token in the URL leaks into access logs, browser history and Referer.
    // clientNonce: per-tab replay-protection secret (binds the token to this
    // browser so a copied auto-login URL can't be replayed elsewhere).
    try {
      const response = await api.post('/auth/officernd-auto-login', {
        memberId,
        memberName,
        token,
        clientNonce,
      });

      logger.info('AUTH', 'OfficeRND auto-login response received', {
        status: response.status,
        hasAccessToken: !!response.data?.access_token,
        hasRefreshToken: !!response.data?.refresh_token,
        userId: response.data?.user?.id,
      });

      // Save tokens if returned
      if (response.data.access_token && response.data.refresh_token) {
        accessToken = response.data.access_token;
        refreshToken = response.data.refresh_token;
        localStorage.setItem('access_token', response.data.access_token);
        localStorage.setItem('refresh_token', response.data.refresh_token);
        logger.success('AUTH', 'OfficeRND auto-login tokens stored');
      } else {
        logger.warn('AUTH', 'OfficeRND auto-login returned no tokens', {
          hasAccessToken: !!response.data?.access_token,
          hasRefreshToken: !!response.data?.refresh_token,
        });
      }

      return response.data;
    } catch (error) {
      logger.error('AUTH', 'OfficeRND auto-login failed', {
        status: error.response?.status,
        message: error.message,
        detail: error.response?.data?.detail,
      });
      throw error;
    }
  },

  sendOTP: async (phoneNumber) => {
    const response = await api.post('/auth/send-otp', {
      phone_number: phoneNumber
    });
    return response.data;
  },

  verifyOTP: async (phoneNumber, otpCode) => {
    const response = await api.post('/auth/verify-otp', {
      phone_number: phoneNumber,
      otp_code: otpCode
    });
    return response.data;
  },
};

// Chat API
export const chatAPI = {
  // ==================== V1 PATTERN-BASED ENDPOINT ====================
  // Phase 1: Detection only - shows function names
  // Phase 2+: Will implement actual business logic
  sendMessage: async (content, signal, attachments = null, buttonPayload = null) => {
    // Get user ID from access token (JWT)
    let userId = null;
    try {
      const accessToken = localStorage.getItem('access_token');
      if (accessToken) {
        // Parse JWT token (base64 decode payload)
        const base64Url = accessToken.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
          atob(base64)
            .split('')
            .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join('')
        );
        const payload = JSON.parse(jsonPayload);
        userId = payload.sub || payload.user_id || payload.id;
      }
    } catch (error) {
      logger.debug('API', 'Failed to parse user ID from token', { error: error.message });
    }

    // Build request payload
    const payload = {
      message: content,  // Changed from 'content' to 'message'
      user_id: userId
    };

    // Add attachments array if provided (support ticket photos/videos)
    logger.info('API', 'sendMessage called', {
      messagePreview: content.substring(0, 50),
      hasAttachments: !!attachments,
      attachmentsType: typeof attachments,
      isArray: Array.isArray(attachments),
      attachmentsLength: attachments ? attachments.length : 0,
      attachments: attachments
    });

    if (attachments && Array.isArray(attachments) && attachments.length > 0) {
      payload.attachments = attachments;
      logger.info('API', '✅ Adding attachments to payload', {
        messagePreview: content.substring(0, 50),
        attachmentCount: attachments.length,
        firstAttachment: attachments[0]
      });
    } else {
      logger.debug('API', 'No attachments on payload', {
        reason: !attachments ? 'attachments is null/undefined' :
                !Array.isArray(attachments) ? 'attachments is not an array' :
                attachments.length === 0 ? 'attachments array is empty' : 'unknown'
      });
    }

    if (buttonPayload) {
      payload.button_payload = buttonPayload;
    }

    logger.info('API', 'Final payload being sent', { payload });

    // Simple one-time retry for chat messages
    // Retry only on network errors or 5xx server errors
    try {
      const response = await api.post('/chat/v1/message', payload, {
        timeout: 120000,
        signal
      });
      return response.data;
    } catch (error) {
      // Simple retry: Only retry on network issues or infrastructure errors
      const isConnectionFailure = !error.response; // Network down, DNS failure, timeout
      const isGatewayError = error.response?.status === 502 || // Bad Gateway
                             error.response?.status === 503 || // Service Unavailable
                             error.response?.status === 504;   // Gateway Timeout
      const wasAborted = signal?.aborted;

      // Don't retry if user cancelled
      if (wasAborted) {
        throw error;
      }

      // Retry on connection failures OR gateway errors (infrastructure issues)
      // Don't retry on 500 (backend code bug) or 4xx (user errors)
      if (isConnectionFailure || isGatewayError) {
        // Wait 1 second and retry once
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Retry the request
        const response = await api.post('/chat/v1/message', payload, {
          timeout: 120000,
          signal
        });
        return response.data;
      }

      // Server error (500) or client error (4xx) - don't retry
      throw error;
    }
  },

  // ==================== OLD AI-BASED ENDPOINT (COMMENTED OUT) ====================
  // sendMessage: async (content, signal) => {
  //   const response = await api.post('/chat/messages', {
  //     content,
  //   }, {
  //     timeout: 120000, // 2 minutes timeout for chat/booking operations
  //     signal
  //   });
  //   return response.data;
  // },
  // ==================== END OLD ENDPOINT ====================

  getMessages: async (params = {}) => {
    const queryParams = {
      limit: params.limit || 50,
      offset: params.offset || 0,
      channel: params.channel // optional: 'web', 'sms', or null for all
    };
    
    // Remove undefined values
    Object.keys(queryParams).forEach(key => {
      if (queryParams[key] === undefined) {
        delete queryParams[key];
      }
    });
    
    const response = await api.get('/chat/messages', {
      params: queryParams,
    });
    return response.data;
  },

  clearHistory: async () => {
    const response = await api.delete('/chat/history');
    return response.data;
  },

  // Deprecated - kept for backward compatibility
  createSession: async (sessionName) => {
    const response = await api.post('/chat/sessions', {
      session_name: sessionName,
    });
    return response.data;
  },

  getSessions: async (limit = 10, offset = 0) => {
    const response = await api.get('/chat/sessions', {
      params: { limit, offset },
    });
    return response.data;
  },

  deleteSession: async (sessionId) => {
    const response = await api.delete(`/chat/sessions/${sessionId}`);
    return response.data;
  },
};

// User API
export const userAPI = {
  getProfile: async () => {
    const response = await api.get('/users/profile');
    return response.data;
  },

  // Proactive notification preferences (Web / SMS switch per type)
  // GET /users/notification-preferences → { types: [{key,label,description,web,sms}] }
  getNotificationPreferences: async () => {
    const response = await api.get('/users/notification-preferences');
    return response.data;
  },

  // PUT /users/notification-preferences { preferences: { [key]: {web?, sms?} }, sms_consent? }
  // Partial; server forces sms=false when web=false. Returns the full set plus
  // `sms` (master SMS state). `smsConsent` is the master "Text messages" switch.
  updateNotificationPreferences: async (preferences, smsConsent) => {
    const body = { preferences: preferences || {} };
    if (typeof smsConsent === 'boolean') body.sms_consent = smsConsent;
    const response = await api.put('/users/notification-preferences', body);
    return response.data;
  },

  updateProfile: async (data, profileImage = null, aiAssistantImage = null) => {
    let response;

    logger.debug('API', 'Profile update initiated', {
      hasProfileImage: !!profileImage,
      hasAIAssistantImage: !!aiAssistantImage,
      fields: Object.keys(data)
    });

    try {
      // Check if we have images to upload
      if (profileImage || aiAssistantImage) {
        // Use FormData for multipart request
        const formData = new FormData();
        if (data.first_name !== undefined && data.first_name !== null) {
          formData.append('first_name', data.first_name);
        }
        if (data.last_name !== undefined && data.last_name !== null) {
          formData.append('last_name', data.last_name);
        }
        if (data.phone_number !== undefined && data.phone_number !== null) {
          formData.append('phone_number', data.phone_number);
        }
        // Note: We're NOT sending email since it can't be changed
        if (profileImage) {
          formData.append('user_image', profileImage);
        }
        if (aiAssistantImage) {
          formData.append('ai_assistant_image', aiAssistantImage);
        }
        
        response = await axios.put(`${API_BASE_URL}/users/profile`, formData, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            // Override the default JSON content type for FormData
            'Content-Type': undefined
            // This will let axios automatically set 'multipart/form-data' with proper boundary
          },
        });
      } else {
        // MUST use FormData even without images because backend expects Form() parameters
        const formData = new FormData();

        // Only include fields that are defined and not empty
        if (data.first_name !== undefined && data.first_name !== null) {
          formData.append('first_name', data.first_name);
        }
        if (data.last_name !== undefined && data.last_name !== null) {
          formData.append('last_name', data.last_name);
        }
        if (data.phone_number !== undefined && data.phone_number !== null) {
          formData.append('phone_number', data.phone_number);
        }
        
        // IMPORTANT: Let axios set the Content-Type with boundary for FormData
        response = await api.put('/users/profile', formData, {
          headers: {
            // Override the default JSON content type for FormData
            'Content-Type': undefined
            // This will let axios automatically set 'multipart/form-data' with proper boundary
          }
        });
      }

logger.debug('API', 'Profile update response', { status: response.status });
      return response.data;
    } catch (error) {
      logger.error('API', 'Profile update failed', {
        status: error.response?.status,
        message: error.message
      });
      throw error;
    }
  },

  changePassword: async (data) => {
    const response = await api.put('/users/change-password', data);
    return response.data;
  },

  completeProfile: async (phoneNumber, smsConsent, userImage, opts = {}) => {
    /**
     * Complete user profile after OfficeRND auto-registration.
     *
     * @param {string}  phoneNumber - Phone number in E.164 format (+1234567890)
     * @param {boolean} smsConsent  - SMS consent (must be true)
     * @param {File}    userImage   - Profile image file (may be null)
     * @param {object}  opts        - { lastOtpError?: string }
     *
     * `lastOtpError` is forwarded only when the FE falls back to §2.4 auto-submit
     * (SEND_LIMIT or 3 wrong codes) so admin can see why Twilio failed. Capped at
     * 500 chars by the backend. Omit on the happy path. See phone-verification
     * frontend instructions §11.
     */
    const formData = new FormData();
    formData.append('phone_number', phoneNumber);
    formData.append('sms_consent', smsConsent);
    if (userImage) {
      formData.append('user_image', userImage);
    }
    if (opts.lastOtpError) {
      formData.append('last_otp_error', String(opts.lastOtpError).slice(0, 500));
    }

    try {
      const response = await api.post('/auth/complete-profile', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      logger.error('API', 'Profile completion failed:', {
        status: error.response?.status,
        message: error.message
      });
      throw error;
    }
  },

  getBookings: async (params = {}) => {
    // Map frontend filter to backend filter_type
    let filter_type = 'upcoming'; // default
    if (params.upcoming_only) {
      filter_type = 'upcoming';
    } else if (params.past_only) {
      filter_type = 'all'; // Show all bookings if past requested
    }

    const queryParams = {
      filter_type: filter_type
    };

    // Use /api/bookings/ endpoint (enhanced with filtering, excludes cancelled, includes images)
    const response = await api.get('/bookings/', { params: queryParams });
    return response.data;
  },
};

// Admin API
export const adminAPI = {
  getUsers: async (params = {}) => {
    const queryParams = {
      page: params.page || 1,
      limit: Math.min(params.limit || 20, 100), // Max 100 as per backend
      search: params.search,
      status: params.status_filter,
      location_id: params.location_id, // officernd_id UUID — filter users by primary location
      // Phone-verification spec §6 — only sent when the "Needs phone review"
      // chip is on; omitted otherwise so the backend returns all users.
      needs_phone_review: params.needs_phone_review || undefined,
      sort_by: params.sort_by || 'created_at',
      sort_order: params.sort_order || 'desc',
      include_stats: false  // Don't load stats in list view (much faster!)
    };

    // Remove undefined values
    Object.keys(queryParams).forEach(key => {
      if (queryParams[key] === undefined) {
        delete queryParams[key];
      }
    });

    const response = await api.get('/admin/users', { params: queryParams });
    return response.data;
  },
  
  getUserDetails: async (userId) => {
    // New endpoint to get single user with full statistics
    const response = await api.get(`/admin/users/${userId}/details`);
    return response.data;
  },

  // A member's proactive notification preferences (same shape as userAPI.*)
  getUserNotificationPreferences: async (userId) => {
    const response = await api.get(`/admin/users/${userId}/notification-preferences`);
    return response.data;
  },

  updateUserNotificationPreferences: async (userId, preferences) => {
    const response = await api.put(`/admin/users/${userId}/notification-preferences`, { preferences });
    return response.data;
  },

  // Admin master SMS switch for a member (User Management). Returns the
  // SmsConsentState; `requires_start_text` = Twilio still blocks the number
  // until the member texts START.
  setUserSmsConsent: async (userId, enabled, note) => {
    const body = { enabled };
    if (note) body.note = note;
    const response = await api.put(`/admin/users/${userId}/sms-consent`, body);
    return response.data;
  },

  approveUser: async (userId) => {
    const response = await api.put(`/admin/users/${userId}/approve`);
    return response.data;
  },

  // Refresh a single user's OfficeRND membership from the live API (1 call).
  syncMembership: async (userId) => {
    const response = await api.post(`/admin/users/${userId}/refresh-membership`);
    return response.data;
  },

  // Start a full OfficeRND membership cache refresh for all matched users.
  // The backend returns immediately ({ started, reason?, status }) and runs the
  // sync in the background — poll getMembershipSyncStatus() until status.running is false.
  syncAllMemberships: async () => {
    const response = await api.post('/admin/users/memberships/sync');
    return response.data;
  },

  // Cross-worker status of the membership sync: { running, last_synced_at, synced_users, last_error, ... }.
  getMembershipSyncStatus: async () => {
    const response = await api.get('/admin/users/memberships/sync/status');
    return response.data;
  },

  rejectUser: async (userId) => {
    const response = await api.put(`/admin/users/${userId}/reject`);
    return response.data;
  },

  blockUser: async (userId) => {
    const response = await api.put(`/admin/users/${userId}/block`);
    return response.data;
  },

  resetUserPassword: async (userId, newPassword) => {
    const response = await api.put(`/admin/users/${userId}/reset-password`, {
      new_password: newPassword
    });
    return response.data;
  },

  updateUserImage: async (userId, file) => {
    const formData = new FormData();
    formData.append('user_image', file);

    const response = await api.put(
      `/admin/users/${userId}/image`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  deleteUser: async (userId) => {
    const response = await api.delete(`/admin/users/${userId}`);
    return response.data;
  },

  restoreUser: async (userId) => {
    const response = await api.put(`/admin/users/${userId}/restore`);
    return response.data;
  },

  unblockUser: async (userId) => {
    const response = await api.put(`/admin/users/${userId}/unblock`);
    return response.data;
  },

  getBackendLogs: async (lines = 100, offset = 0) => {
    const response = await api.get('/admin/logs/backend', {
      params: { lines, offset }
    });
    return response.data;
  },

  getFrontendLogs: async (lines = 100, offset = 0, filters = {}) => {
    const params = { lines, offset };
    if (filters.user_id) params.user_id = filters.user_id;
    if (filters.level) params.level = filters.level;

    const response = await api.get('/admin/logs/frontend', { params });
    return response.data;
  },

  submitFrontendLogs: async (logEntries) => {
    const response = await api.post('/admin/logs/frontend', logEntries);
    return response.data;
  },

  // Developer API endpoints for testing
  getDeveloperMembers: async () => {
    const response = await api.get('/admin/developer/members');
    return response;
  },

  getDeveloperCompanies: async () => {
    const response = await api.get('/admin/developer/companies');
    return response;
  },

  getDeveloperResources: async (params = {}) => {
    const queryParams = {};
    if (params.room_type) queryParams.room_type = params.room_type;
    if (params.location_id) queryParams.location_id = params.location_id;
    
    const response = await api.get('/admin/developer/resources', { params: queryParams });
    return response;
  },

  getDeveloperResourceTypes: async () => {
    const response = await api.get('/admin/developer/resource-types');
    return response;
  },

  getDeveloperLocations: async () => {
    const response = await api.get('/admin/developer/locations');
    return response;
  },

  getDeveloperStatus: async () => {
    const response = await api.get('/admin/developer/status');
    return response;
  },

  // Admin Monitoring API endpoints for viewing user conversations
  monitoring: {
    /**
     * Get all conversations for a specific user
     * @param {number} userId - The ID of the user
     * @param {Object} params - Query parameters (limit, offset, channel)
     * @returns {Promise} Conversation list
     */
    getUserConversations: async (userId, params = {}) => {
      const queryParams = {
        limit: params.limit || 50,
        offset: params.offset || 0,
        channel: params.channel // 'web' or 'sms', optional
      };

      // Remove undefined values
      Object.keys(queryParams).forEach(key => {
        if (queryParams[key] === undefined) {
          delete queryParams[key];
        }
      });

      const response = await api.get(`/admin/monitoring/users/${userId}/conversations`, {
        params: queryParams
      });
      return response.data;
    },

    /**
     * Get messages from a specific conversation
     * @param {number} conversationId - The ID of the conversation
     * @param {string} type - Must be 'web' or 'sms'
     * @param {Object} params - Query parameters (limit, offset)
     * @returns {Promise} Message list with conversation info
     */
    getConversationMessages: async (conversationId, type, params = {}) => {
      if (!type) {
        throw new Error('Conversation type (web or sms) is required');
      }

      const queryParams = {
        type,
        limit: params.limit || 100,
        offset: params.offset || 0
      };

      const response = await api.get(`/admin/monitoring/conversations/${conversationId}/messages`, {
        params: queryParams
      });
      return response.data;
    },

    /**
     * Export a conversation in JSON or CSV format
     * @param {number} conversationId - The ID of the conversation
     * @param {string} type - Must be 'web' or 'sms'
     * @param {string} format - Must be 'json' or 'csv'
     * @returns {Promise} Export data (JSON object or CSV blob)
     */
    exportConversation: async (conversationId, type, format) => {
      if (!type) {
        throw new Error('Conversation type (web or sms) is required');
      }
      if (!format) {
        throw new Error('Export format (json or csv) is required');
      }
      if (format !== 'json' && format !== 'csv') {
        throw new Error('Invalid export format. Must be json or csv');
      }

      const response = await api.get(`/admin/monitoring/conversations/${conversationId}/export`, {
        params: { type, format },
        responseType: format === 'csv' ? 'blob' : 'json'
      });
      return response.data;
    },

    /**
     * Get current Twilio account balance.
     * Backend caches 30s. 503 → unavailable, 403 → caller lacks system:read.
     * Pass { skipErrorToast: true } so callers can silently hide the card on error.
     * @returns {Promise<{account_sid:string, balance:string, balance_float:number, currency:string, low_balance:boolean, fetched_at:string}>}
     */
    getTwilioBalance: async (options = {}) => {
      const response = await api.get('/admin/monitoring/twilio/balance', options);
      return response.data;
    }
  }
};

// Analytics API
export const analyticsAPI = {
  // Admin Analytics endpoints (matching backend API from DASHBOARD_API_GUIDE.md)
  // All accept optional { skipErrorToast: true } to silence 403 toasts for background loads
  getDashboard: async (options = {}) => {
    const response = await api.get('/analytics/admin/dashboard', options);
    return response.data.data;
  },

  getUserAnalytics: async (userId, options = {}) => {
    const response = await api.get(`/analytics/admin/users/${userId}`, options);
    return response.data.data;
  },

  getBookingTrends: async (days = 30, options = {}) => {
    const response = await api.get(`/analytics/admin/bookings/trends?days=${days}`, options);
    return response.data.data;
  },

  getResourceAnalytics: async (options = {}) => {
    const response = await api.get('/analytics/admin/resources', options);
    return response.data.data;
  },

  getSystemHealth: async (options = {}) => {
    const response = await api.get('/analytics/admin/health', options);
    return response.data.data;
  },

  /**
   * Bookings made through the concierge, newest first, for the dashboard
   * card's drill-down modal.
   *
   * Search, ordering, paging and the caller's location permissions are all
   * applied server-side — the client never filters for correctness. Times come
   * back pre-formatted in the booking's own timezone, so don't re-convert them.
   *
   * The window defaults to ALL TIME, like the card that opens it — see
   * getFaqQueries, which established this contract.
   *
   * @param {Object} params
   * @param {string} [params.range='lifetime'] 'lifetime' | '24h' | '7d' | '30d'
   * @param {string} [params.from]       ISO start of the booked-at window
   * @param {string} [params.to]         ISO end of the booked-at window
   * @param {string} [params.q]          Member, email, room, location, reference or id
   * @param {string} [params.locationId] Narrows within the caller's scope
   * @param {string[]} [params.statuses] confirmed / updated / cancelled; omit for all
   * @param {number} [params.limit=10]
   * @param {string} [params.cursor]     Opaque cursor from the previous page
   * @param {AbortSignal} [params.signal]
   */
  getConciergeBookings: async (params = {}) => {
    const {
      range, from, to, q, locationIds, statuses, limit, cursor, signal, ...options
    } = params;
    const query = {};
    // A NAMED window, not an hour count: 'lifetime' has to survive the trip as
    // a word, or both ends would have to agree forever on which very large
    // number means "no lower bound".
    if (range) query.range = range;
    if (from) query.from = from;
    if (to) query.to = to;
    if (q) query.q = q;
    // Repeated params (?location_id=a&location_id=b) — axios serialises an
    // array this way, which is what FastAPI's List[str] expects.
    if (locationIds?.length) query.location_id = locationIds;
    // Same shape, same reason. Absent means every status, which is what the
    // card counts — not "confirmed only".
    if (statuses?.length) query.status = statuses;
    if (limit) query.limit = limit;
    if (cursor) query.cursor = cursor;

    const response = await api.get('/analytics/admin/concierge-bookings', {
      params: query,
      // Repeat the key instead of bracketing it: axios defaults to
      // `location_id[]=x`, which FastAPI's List[str] query param ignores — the
      // filter then silently does nothing and every location comes back.
      paramsSerializer: { indexes: null },
      signal,
      ...options,
    });
    return response.data.data;
  },

  /**
   * Full detail for one concierge booking: the booking, the member (live phone
   * and home location) and the room (current amenities and capacity).
   */
  getConciergeBookingDetail: async (bookingRowId, options = {}) => {
    const response = await api.get(
      `/analytics/admin/concierge-bookings/${bookingRowId}`,
      options
    );
    return response.data.data;
  },

  /** Locations for the drill-down filter — already trimmed to the caller's scope. */
  getConciergeBookingLocations: async (options = {}) => {
    const response = await api.get(
      '/analytics/admin/concierge-bookings/locations',
      options
    );
    return response.data.data;
  },

  /**
   * Support tickets members reported, newest first, for the dashboard card's
   * drill-down panel.
   *
   * Same contract as getConciergeBookings — search, ordering, paging and the
   * caller's location scope are all server-side, and times come back
   * pre-formatted in the TICKET's own timezone. Do not re-convert them.
   *
   * The window defaults to ALL TIME, like the card that opens it.
   *
   * @param {Object} params
   * @param {string} [params.range='lifetime'] 'lifetime' | '24h' | '7d' | '30d'
   * @param {string} [params.from]            ISO start of the reported-at window
   * @param {string} [params.to]              ISO end of the reported-at window
   * @param {string} [params.q]               Name, phone, email, location, issue text or ticket id
   * @param {string[]} [params.locationIds]   Narrows within the caller's scope
   * @param {string} [params.responseStatus]  'awaiting' | 'answered'
   * @param {number} [params.limit=20]
   * @param {string} [params.cursor]          Opaque cursor from the previous page
   * @param {AbortSignal} [params.signal]
   */
  getSupportTickets: async (params = {}) => {
    const {
      range, from, to, q, locationIds, responseStatus, reporterType,
      helpdeskStatuses, limit, cursor, signal, ...options
    } = params;
    const query = {};
    // A NAMED window — see getConciergeBookings.
    if (range) query.range = range;
    if (from) query.from = from;
    if (to) query.to = to;
    if (q) query.q = q;
    if (locationIds?.length) query.location_id = locationIds;
    if (responseStatus) query.response_status = responseStatus;
    if (reporterType) query.reporter_type = reporterType;
    // Repeated param, like location_id — FastAPI's List[str] ignores the
    // bracketed form axios produces by default.
    if (helpdeskStatuses?.length) query.helpdesk_status = helpdeskStatuses;
    if (limit) query.limit = limit;
    if (cursor) query.cursor = cursor;

    const response = await api.get('/analytics/admin/support-tickets', {
      params: query,
      // Repeat the key rather than bracketing it — axios' default
      // `location_id[]=x` is ignored by FastAPI's List[str], and the filter
      // then silently does nothing.
      paramsSerializer: { indexes: null },
      signal,
      ...options,
    });
    return response.data.data;
  },

  /**
   * Full detail for one support ticket: the report and its photos, the
   * reporter (live phone and home location), who was notified and whether they
   * answered, the replies that reached the reporter, and a timeline.
   */
  getSupportTicketDetail: async (ticketRowId, options = {}) => {
    const response = await api.get(
      `/analytics/admin/support-tickets/${ticketRowId}`,
      options
    );
    return response.data.data;
  },

  /**
   * FAQ questions members asked, newest first, for the FAQ card's drill-down.
   *
   * Same contract as the other drill-downs — search, ordering, paging and the
   * caller's location scope are all server-side, and `asked_at_display` comes
   * back pre-formatted in the LOCATION's timezone. Do not re-convert it.
   *
   * Differs from its siblings in one way: the window defaults to ALL TIME, not
   * 24 hours, because the card it opens counts all time and the two must agree.
   *
   * @param {Object} params
   * @param {string} [params.range='lifetime'] 'lifetime' | '24h' | '7d' | '30d'
   * @param {string} [params.from]          ISO start of the asked-at window
   * @param {string} [params.to]            ISO end of the asked-at window
   * @param {string} [params.q]             Member, email, phone, question or answer
   * @param {string[]} [params.locationIds] Narrows within the caller's scope
   * @param {string} [params.channel]       'web' | 'sms'
   * @param {string} [params.resolution]    'answered' | 'unanswered', or an exact value
   * @param {number} [params.limit=10]
   * @param {string} [params.cursor]        Opaque cursor from the previous page
   * @param {AbortSignal} [params.signal]
   */
  getFaqQueries: async (params = {}) => {
    const {
      range, from, to, q, locationIds, channel, resolution, limit, cursor,
      signal, ...options
    } = params;
    const query = {};
    if (range) query.range = range;
    if (from) query.from = from;
    if (to) query.to = to;
    if (q) query.q = q;
    if (locationIds?.length) query.location_id = locationIds;
    if (channel) query.channel = channel;
    if (resolution) query.resolution = resolution;
    if (limit) query.limit = limit;
    if (cursor) query.cursor = cursor;

    const response = await api.get('/analytics/admin/faq-queries', {
      params: query,
      // Repeat the key rather than bracketing it — axios' default
      // `location_id[]=x` is ignored by FastAPI's List[str], and the filter
      // then silently does nothing.
      paramsSerializer: { indexes: null },
      signal,
      ...options,
    });
    return response.data.data;
  },

  /**
   * Full detail for one FAQ question: the untruncated question and answer, how
   * it matched, and which FAQs the organizer had in front of it.
   *
   * Does NOT include the system steps — those are a separate call the dialog
   * makes only when an admin expands them.
   */
  getFaqQueryDetail: async (messageId, options = {}) => {
    const response = await api.get(
      `/analytics/admin/faq-queries/${messageId}`,
      options
    );
    return response.data.data;
  },

  /**
   * Meetings members extended by replying YES to the ending-soon reminder,
   * newest first, for the dashboard card's drill-down panel.
   *
   * Same contract as getConciergeBookings — search, ordering, paging and the
   * caller's location scope are all server-side, and times come back
   * pre-formatted in the BOOKING's own timezone. Do not re-convert them.
   *
   * The window defaults to ALL TIME, like the card that opens it.
   *
   * @param {Object} params
   * @param {string} [params.range='lifetime'] 'lifetime' | '24h' | '7d' | '30d'
   * @param {string} [params.from]          ISO start of the extended-at window
   * @param {string} [params.to]            ISO end of the extended-at window
   * @param {string} [params.q]             Member, email, room, location or reference
   * @param {string[]} [params.locationIds] Narrows within the caller's scope
   * @param {string} [params.outcome]       'extended' (default) | 'failed' | 'all'
   * @param {number} [params.limit=20]
   * @param {string} [params.cursor]        Opaque cursor from the previous page
   * @param {AbortSignal} [params.signal]
   */
  getBookingExtensions: async (params = {}) => {
    const {
      range, from, to, q, locationIds, outcome, limit, cursor, signal, ...options
    } = params;
    const query = {};
    // A NAMED window — see getConciergeBookings.
    if (range) query.range = range;
    if (from) query.from = from;
    if (to) query.to = to;
    if (q) query.q = q;
    if (locationIds?.length) query.location_id = locationIds;
    if (outcome) query.outcome = outcome;
    if (limit) query.limit = limit;
    if (cursor) query.cursor = cursor;

    const response = await api.get('/analytics/admin/booking-extensions', {
      params: query,
      // Repeat the key rather than bracketing it — axios' default
      // `location_id[]=x` is ignored by FastAPI's List[str], and the filter
      // then silently does nothing.
      paramsSerializer: { indexes: null },
      signal,
      ...options,
    });
    return response.data.data;
  },

  /**
   * Full detail for one extension: the before/after times, the member (live
   * phone and home location), the room (current amenities and capacity) and
   * the reminder → reply → extended timeline.
   */
  getBookingExtensionDetail: async (eventId, options = {}) => {
    const response = await api.get(
      `/analytics/admin/booking-extensions/${eventId}`,
      options
    );
    return response.data.data;
  },

  getAllUsersAnalytics: async (params = {}) => {
    const { limit = 50, offset = 0, sort_by = 'total_bookings', sort_order = 'desc', skipErrorToast } = params;
    const response = await api.get(
      `/analytics/admin/users?limit=${limit}&offset=${offset}&sort_by=${sort_by}&sort_order=${sort_order}`,
      skipErrorToast ? { skipErrorToast: true } : {}
    );
    return response.data.data;
  },

  // User Analytics endpoints
  getUserProfile: async (userId = null) => {
    // If userId is provided, use it; otherwise use the current user endpoint
    const url = userId ? `/analytics/admin/users/${userId}` : '/analytics/user/profile';
    const response = await api.get(url);
    if (!response.data.success || !response.data.data) {

      // NOTE: Do NOT log response.data - it may contain PII
      logger.error('API', 'Invalid profile response structure', { success: response.data.success });
      return null;
    }
    return response.data.data; // Extract data from success response
  },

  getUserBookingSummary: async () => {
    const response = await api.get('/analytics/user/bookings/summary');
    if (!response.data.success || !response.data.data) {

      // NOTE: Do NOT log response.data - it may contain PII
      logger.error('API', 'Invalid booking summary response structure', { success: response.data.success });
      return null;
    }
    return response.data.data; // Extract data from success response
  },

  // Event Tracking
  trackEvent: async (eventData) => {
    const response = await api.post('/analytics/track', eventData);
    return response.data;
  },

  // Cache Management (Admin only)
  clearCache: async (cacheType) => {
    const response = await api.post(`/analytics/admin/cache/clear?cache_type=${cacheType}`);
    return response.data;
  },

  refreshUserCache: async (userId) => {
    const response = await api.post(`/analytics/admin/cache/refresh/${userId}`);
    return response.data;
  },

  // User Coins endpoint
  getUserCoins: async () => {
    try {
      const response = await api.get('/analytics/user/coins');
      // NOTE: Do NOT log response.data - it may contain PII
      logger.debug('API', 'User coins fetched', { status: response.status });
      return response.data;
    } catch (error) {
      // NOTE: Do NOT log full error object - it may contain PII
      logger.error('API', 'Failed to fetch user coins', { message: error.message });
      // Return error response instead of throwing to prevent blocking
      return {
        success: false,
        message: 'Failed to fetch coins',
        data: {
          month: new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
          total_coins: 'N/A'
        }
      };
    }
  },
};

// Location API
/**
 * SMS cost reporting (admin-only, read-only).
 *
 * Every response carries BOTH `total_cost_usd` (what Twilio charged us) and
 * `attributed_cost_usd` (what counts toward the member under the current admin
 * setting). Money arrives as exact STRINGS — never coerce it to a Number to do
 * arithmetic; the backend has already done the maths.
 */
export const smsCostAPI = {
  getSummary: async (range = '30d', options = {}) => {
    const response = await api.get(`/analytics/sms-cost/summary?range=${range}`, options);
    return response.data.data;
  },

  // Scroll-loaded: returns {items, total, next_cursor, has_more} — the shape
  // useInfiniteList expects, same as every other admin drill-down. Keyset, not
  // offset, so rows are never repeated or skipped while an admin scrolls.
  getUsers: async ({
    range = 'lifetime', search, locationId, sort = 'cost_desc',
    from, to, cursor, limit = 10, signal,
  } = {}) => {
    const params = new URLSearchParams({ range, sort, limit: String(limit) });
    if (search) params.set('search', search);
    if (locationId) params.set('location_id', locationId);
    // Explicit bounds win over the named window on the server.
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (cursor) params.set('cursor', cursor);
    const response = await api.get(
      `/analytics/sms-cost/users?${params.toString()}`, { signal }
    );
    return response.data.data;
  },

  getUserDetail: async (userId, range = '30d', options = {}) => {
    const response = await api.get(
      `/analytics/sms-cost/users/${userId}?range=${range}`,
      options
    );
    return response.data.data;
  },

  // Batch: one request for every member currently on screen. Deliberately
  // separate from the conversation-list query, which is already expensive.
  getByUsers: async (userIds = [], range = '30d', options = {}) => {
    if (!userIds.length) return { users: {} };
    const ids = userIds.join(',');
    const response = await api.get(
      `/analytics/sms-cost/by-users?user_ids=${ids}&range=${range}`,
      options
    );
    return response.data.data;
  },
};

export const locationAPI = {
  // Admin endpoints
  syncLocations: async (provider = 'officernd', force = false) => {
    const response = await api.post('/admin/locations/sync', { provider, force });
    return response.data;
  },

  getAdminLocations: async (params = {}) => {
    const queryParams = {
      page: params.page || 1,
      page_size: params.page_size || 20,
      status: params.status,
      provider: params.provider,
      search: params.search,
      is_open: params.is_open,
      is_public: params.is_public
    };
    
    // Remove undefined values
    Object.keys(queryParams).forEach(key => {
      if (queryParams[key] === undefined) {
        delete queryParams[key];
      }
    });
    
    const response = await api.get('/admin/locations', { params: queryParams });
    return response.data;
  },

  getLocation: async (locationId) => {
    const response = await api.get(`/admin/locations/${locationId}`);
    return response.data;
  },

  updateLocation: async (locationId, updates) => {
    const response = await api.put(`/admin/locations/${locationId}`, updates);
    return response.data;
  },

  bulkUpdateLocations: async (locationIds, updates) => {
    const response = await api.post('/admin/locations/bulk-update', {
      location_ids: locationIds,
      updates
    });
    return response.data;
  },

  // Per-location contacts (DYNAMIC_SETTINGS_FRONTEND_PLAN.md §13) — emails/phones/slack
  // overrides stored per location. Gated by settings:read (GET) / settings:write (PUT).
  getLocationContacts: async () => {
    const response = await api.get('/admin/locations/contacts');
    return response.data;
  },

  getLocationContact: async (officerndId) => {
    const response = await api.get(`/admin/locations/${officerndId}/contact`);
    return response.data;
  },

  updateLocationContact: async (officerndId, contact) => {
    const response = await api.put(`/admin/locations/${officerndId}/contact`, contact);
    return response.data;
  },

  // Public endpoints
  getActiveLocations: async () => {
    const response = await api.get('/admin/locations/public/active');
    return response.data;
  }
};

// Community Team API — central per-location staff list (community:read/manage).
// Backend: concierge-fast-api app/routers/admin/admin_community.py
export const communityAPI = {
  getOverview: async () => {
    const response = await api.get('/admin/community/overview');
    return response.data;
  },

  // Bulk add — entries: [{value, display_name?, position_id?}] (emails or domains)
  addMembers: async (officerndId, entries) => {
    const response = await api.post(`/admin/community/${officerndId}/members`, { entries });
    return response.data;
  },

  updateMember: async (memberId, updates) => {
    const response = await api.patch(`/admin/community/members/${memberId}`, updates);
    return response.data;
  },

  removeMember: async (memberId) => {
    const response = await api.delete(`/admin/community/members/${memberId}`);
    return response.data;
  },

  getPositions: async () => {
    const response = await api.get('/admin/community/positions');
    return response.data;
  },

  createPosition: async (name) => {
    const response = await api.post('/admin/community/positions', { name });
    return response.data;
  },

  // updates: {name?, display_order?, is_active?}
  updatePosition: async (positionId, updates) => {
    const response = await api.patch(`/admin/community/positions/${positionId}`, updates);
    return response.data;
  },

  deletePosition: async (positionId) => {
    const response = await api.delete(`/admin/community/positions/${positionId}`);
    return response.data;
  },

  // Registered-user typeahead for the chip input
  suggest: async (q) => {
    const response = await api.get('/admin/community/suggest', { params: { q } });
    return response.data;
  }
};

// Resource API
export const resourceAPI = {
  // Admin endpoints

  /**
   * Sync resources from OfficeRND
   * @param {string} provider - Provider name (default: 'officernd')
   * @param {boolean} force - Force sync even if locked (default: false)
   * @returns {Promise<Object>} Sync result with statistics
   */
  syncResources: async (provider = 'officernd', force = false) => {
    logger.debug('RESOURCEAPI', 'Syncing resources', { provider, force });
    try {
      const response = await api.post('/admin/resources/sync', { provider, force }, { timeout: 900000 });
      logger.success('RESOURCEAPI', 'Resources synced successfully', {
        synced: response.data.synced,
        created: response.data.created,
        updated: response.data.updated,
        errors: response.data.errors
      });
      return response.data;
    } catch (error) {
      logger.error('RESOURCEAPI', 'Resource sync failed', { error });
      throw error;
    }
  },

  /**
   * Sync every OfficeRND resource rate into our DB ("Sync Rates" button).
   * @returns {Promise<Object>} {fetched, created, updated, deactivated, skipped}
   */
  syncRates: async () => {
    logger.debug('RESOURCEAPI', 'Syncing rates');
    try {
      const response = await api.post('/admin/resources/rates/sync');
      logger.success('RESOURCEAPI', 'Rates synced', response.data);
      return response.data;
    } catch (error) {
      logger.error('RESOURCEAPI', 'Rate sync failed', { error });
      throw error;
    }
  },

  /**
   * Get paginated resources with filters
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.page_size - Items per page (default: 10)
   * @param {string} params.location_officernd_id - Filter by location UUID
   * @param {string} params.resource_type - Filter by type (meeting_room, hot_desk, private_office)
   * @param {string} params.search - Search term for name/location
   * @returns {Promise<Object>} Resources array with pagination metadata
   */
  getAdminResources: async (params = {}) => {
    const queryParams = {
      page: params.page || 1,
      page_size: params.page_size || 10,
      location_officernd_id: params.location_officernd_id,
      resource_type: params.resource_type,
      search: params.search
    };

    // Remove undefined values
    Object.keys(queryParams).forEach(key => {
      if (queryParams[key] === undefined) {
        delete queryParams[key];
      }
    });

    logger.debug('RESOURCEAPI', 'Fetching resources', { params: queryParams });

    try {
      const response = await api.get('/admin/resources', { params: queryParams });
      logger.success('RESOURCEAPI', 'Resources fetched', {
        total: response.data.total,
        count: response.data.resources?.length
      });
      return response.data;
    } catch (error) {
      logger.error('RESOURCEAPI', 'Failed to fetch resources', { error });
      throw error;
    }
  },

  /**
   * Get single resource by OfficeRND ID
   * @param {string} officerndId - OfficeRND UUID of the resource
   * @returns {Promise<Object>} Resource details
   */
  getResource: async (officerndId) => {
    logger.debug('RESOURCEAPI', 'Fetching resource details', { officerndId });
    try {
      const response = await api.get(`/admin/resources/${officerndId}`);
      logger.success('RESOURCEAPI', 'Resource details fetched', {
        name: response.data.name
      });
      return response.data;
    } catch (error) {
      logger.error('RESOURCEAPI', 'Failed to fetch resource', { error });
      throw error;
    }
  }
};


// Workspace Documents API
export const workspaceDocumentsAPI = {
  // Upload documents (single or multiple files to multiple locations)
  uploadDocuments: async (files, locationIds, metadata = {}, onUploadProgress) => {
    const formData = new FormData();
    
    // Add files (can be single or multiple)
    files.forEach(file => {
      formData.append('files', file);
    });
    
    // Add location IDs as JSON string
    formData.append('location_ids', JSON.stringify(locationIds));
    
    // Optional fields
    if (metadata.name) {
      formData.append('name', metadata.name); // Only used for single file
    }
    if (metadata.description) {
      formData.append('description', metadata.description);
    }
    if (metadata.tags) {
      formData.append('tags', JSON.stringify(metadata.tags));
    }
    
    // Use api instance for multipart/form-data with extended timeout
    const response = await api.post('/workspace-documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 300000, // 5 minutes timeout for file uploads
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        logger.debug('Upload Progress', `${percentCompleted}% completed`);
        // Call the provided callback if available
        if (onUploadProgress) {
          onUploadProgress(percentCompleted, progressEvent);
        }
      }
    });
    
    return response.data;
  },
  
  // Get all documents with pagination
  listDocuments: async (options = {}) => {
    const params = {
      page: options.page || 1,
      page_size: options.pageSize || 20,
      ...(options.locationId && { location_id: options.locationId }),
      ...(options.search && { search: options.search }),
    };
    
    const response = await api.get('/workspace-documents', { params });
    return response.data;
  },
  
  // Get single document with location information
  getDocument: async (documentId) => {
    const response = await api.get(`/workspace-documents/${documentId}`);
    return response.data;
  },
  
  // Replace document file
  replaceDocumentFile: async (documentId, newFile) => {
    const formData = new FormData();
    formData.append('file', newFile);
    
    const response = await api.put(`/workspace-documents/${documentId}/file`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  },
  
  // Add document to more locations
  addDocumentToLocations: async (documentId, locationIds) => {
    const response = await api.post(`/workspace-documents/${documentId}/locations`, {
      location_ids: locationIds
    });
    return response.data;
  },
  
  // Remove document from specific locations
  removeDocumentFromLocations: async (documentId, locationIds) => {
    const response = await api.delete(`/workspace-documents/${documentId}/locations`, {
      data: { location_ids: locationIds }
    });
    return response.data;
  },
  
  // Get documents by location
  getLocationDocuments: async (locationId) => {
    const response = await api.get(`/locations/${locationId}/documents`);
    return response.data;
  },
  
  // Delete document
  deleteDocument: async (documentId) => {
    const response = await api.delete(`/workspace-documents/${documentId}`);
    return response.data;
  },
  
  // Update document metadata
  updateDocument: async (documentId, updates) => {
    const response = await api.put(`/workspace-documents/${documentId}`, updates);
    return response.data;
  }
};

// Text Document API
export const textDocumentAPI = {
  // Create a new text document
  createTextDocument: async (data) => {
    // Validate required fields
    if (!data.name?.trim() || !data.content?.trim() || !data.location_ids?.length) {
      throw new Error('Missing required fields');
    }

    // Validate content size (max 500KB)
    const contentSize = new Blob([data.content]).size;
    if (contentSize > 500 * 1024) {
      throw new Error(`Content exceeds 500KB limit (${(contentSize/1024).toFixed(0)}KB)`);
    }

    // Use the correct API endpoint as per backend documentation
    const response = await api.post('/workspace-documents/text', data);
    return response.data;
  },

  // Edit an existing text document
  editTextDocument: async (documentId, data) => {
    // Content is required for editing
    if (!data.content) {
      throw new Error('Content is required for editing');
    }

    // Validate content size if provided
    if (data.content) {
      const contentSize = new Blob([data.content]).size;
      if (contentSize > 500 * 1024) {
        throw new Error(`Content exceeds 500KB limit (${(contentSize/1024).toFixed(0)}KB)`);
      }
    }

    // Use the correct API endpoint as per backend documentation
    const response = await api.put(`/workspace-documents/${documentId}/text`, data);
    return response.data;
  },

  // Get text document content
  getTextDocumentContent: async (documentId) => {
    // Use the correct API endpoint as per backend documentation
    const response = await api.get(`/workspace-documents/${documentId}/text`);
    return response.data;
  },

  // Validate text document data
  validateTextDocument: (data) => {
    const errors = [];

    if (!data.name?.trim()) {
      errors.push('Document name is required');
    }

    if (!data.content?.trim()) {
      errors.push('Content cannot be empty');
    }

    if (data.content) {
      const contentSize = new Blob([data.content]).size;
      if (contentSize > 500 * 1024) {
        errors.push(`Content size (${(contentSize/1024).toFixed(0)}KB) exceeds 500KB limit`);
      }
    }

    if (!data.location_ids?.length) {
      errors.push('At least one location must be selected');
    }

    return errors;
  },

  // Check if a document is a text document
  isTextDocument: (document) => {
    // Check multiple possible indicators
    return document?.metadata?.source === 'text_input' || 
           document?.file_type === 'text' ||
           document?.file_type === 'txt' ||
           document?.original_filename?.endsWith('.txt') ||
           (document?.name?.includes('Text Document') && !document?.original_filename);
  }
};

// Admin Documents API - for editing uploaded document text
export const adminDocumentsAPI = {
  // Get extracted text from any document (including PDFs)
  getExtractedText: async (documentId) => {
    const response = await api.get(`/workspace-documents/admin/documents/${documentId}/extracted-text`);
    return response.data;
  },
  
  // Update document text (replaces chunks and vectors)
  updateDocumentText: async (documentId, data) => {
    // Validate content size (max 10MB as per backend)
    if (data.content) {
      const contentSize = new Blob([data.content]).size;
      if (contentSize > 10 * 1024 * 1024) {
        throw new Error(`Content exceeds 10MB limit (${(contentSize/(1024*1024)).toFixed(1)}MB)`);
      }
    }
    
    const response = await api.put(`/workspace-documents/admin/documents/${documentId}/update-text`, {
      content: data.content,
      preserve_original: data.preserve_original !== false // Default to true
    });
    return response.data;
  }
};

// Migration API
export const migrationAPI = {
  // Get migration status (no auth required)
  getStatus: async () => {
    const response = await api.get('/admin/migrate/status');
    return response.data;
  },
  
  // Run migration with options (auth required)
  runMigration: async (options = {}) => {
    const { dryRun = false, backup = true, force = false } = options;
    
    const response = await api.post('/admin/migrate', {
      dry_run: dryRun,
      backup: backup,
      force: force
    });
    
    return response.data;
  },
  
  // Check migration health (auth required)
  checkHealth: async () => {
    const response = await api.get('/admin/migrate/health');
    return response.data;
  },
  
  // Repair migration chain (auth required)
  repairMigration: async (force = false) => {
    const response = await api.post('/admin/migrate/repair', {
      force: force
    });
    
    return response.data;
  },
  
  // Rollback migration (auth required - not allowed in production)
  rollbackMigration: async (targetRevision) => {
    const response = await api.post('/admin/migrate/rollback', {
      target: targetRevision
    });
    
    return response.data;
  },
  
  // Legacy methods for backward compatibility
  getMigrationStatus: async () => {
    return migrationAPI.getStatus();
  },
  
  previewMigrations: async () => {
    return migrationAPI.runMigration({ dryRun: true });
  },
  
  applyMigrations: async () => {
    return migrationAPI.runMigration({ dryRun: false });
  }
};

// Helper function to clean query parameters
// Removes undefined, null, empty strings, and whitespace-only strings
// But keeps false for boolean values
const cleanQueryParams = (params) => {
  const cleaned = {};
  Object.keys(params).forEach(key => {
    const value = params[key];
    // Keep the value if it's:
    // - Not undefined, null, or empty string
    // - Not a whitespace-only string
    // - A boolean (even if false)
    if (typeof value === 'boolean') {
      cleaned[key] = value;
    } else if (value !== undefined && value !== null && value !== '') {
      // For strings, also check if it's not just whitespace
      if (typeof value === 'string' && value.trim() === '') {
        // Skip whitespace-only strings
        return;
      }
      cleaned[key] = value;
    }
  });
  return cleaned;
};

// Gantt Chart API (Matching backend documentation)
export const ganttAPI = {
  // Get Gantt chart data with optional filters
  getGanttData: async (params = {}) => {
    const queryParams = {
      start_date: params.start_date,
      end_date: params.end_date,
      location_id: params.location_id
    };
    
    // Clean parameters to remove empty values
    const cleanedParams = cleanQueryParams(queryParams);
    
    const response = await api.get('/gantt/data', { params: cleanedParams });
    return response.data;
  },

  // Get Gantt summary statistics with days ahead parameter
  getGanttSummary: async (daysAhead = 30) => {
    const response = await api.get('/gantt/summary', {
      params: { days_ahead: daysAhead }
    });
    return response.data;
  },

  // Export Gantt data (placeholder - returns preview)
  exportGanttData: async (data) => {
    const response = await api.post('/gantt/export', {
      format: data.format,
      startDate: data.startDate,
      endDate: data.endDate,
      locationId: data.locationId || null
    });
    return response.data;
  }
};

// Google Calendar API
export const googleCalendarAPI = {
  // Connect Google Calendar - returns OAuth URL
  connectCalendar: async () => {
    const response = await api.post('/google-calendar/connect');
    return response.data;
  },

  // Connect Google Calendar for iframe context - returns OAuth URL with popup details
  connectCalendarIframe: async () => {
    try {
      const response = await api.post('/google-calendar/connect/iframe');
      return response.data;
    } catch (error) {
      // Fallback to normal endpoint if iframe endpoint doesn't exist
      if (error.response?.status === 404) {

logger.warn('API', 'Iframe endpoint not found, falling back to normal endpoint');
        const response = await api.post('/google-calendar/connect');
        // Add popup_id for compatibility
        return {
          ...response.data,
          popup_id: response.data.state // Use state as popup_id
        };
      }
      throw error;
    }
  },


  // Retry connection with fresh consent (forces new permission request)
  retryConnect: async () => {
    const response = await api.post('/google-calendar/connect/retry');
    return response.data;
  },

  // Handle OAuth callback
  handleOAuthCallback: async (code, state) => {
    const response = await api.get('/google-calendar/callback', {
      params: { code, state }
    });
    return response.data;
  },

  // Get user's calendar integrations
  getIntegrations: async () => {
    const response = await api.get('/google-calendar/integrations');
    return response.data;
  },

  // Get events from specific calendar
  getCalendarEvents: async (integrationId, params = {}) => {
    const queryParams = {
      start_date: params.start_date,
      end_date: params.end_date,
      include_private: params.include_private || false
    };
    
    // Clean parameters to remove empty values
    const cleanedParams = cleanQueryParams(queryParams);
    
    const response = await api.get(`/google-calendar/integrations/${integrationId}/events`, {
      params: cleanedParams
    });
    return response.data;
  },

  // Get all events from all calendars
  getAllEvents: async (params = {}) => {
    const queryParams = {
      start_date: params.start_date,
      end_date: params.end_date,
      include_private: params.include_private || false
    };
    
    // Clean parameters to remove empty values
    const cleanedParams = cleanQueryParams(queryParams);
    
    const response = await api.get('/google-calendar/events/all', { params: cleanedParams });
    return response.data;
  },

  // Sync calendar (with rate limit handling)
  syncCalendar: async (integrationId) => {
    try {
      const response = await api.post(`/google-calendar/integrations/${integrationId}/sync`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 429) {
        // Extract wait time from message and create user-friendly error
        const message = error.response.data.detail || 'Rate limited';
        const match = message.match(/(\d+)\s+minutes?/);
        const waitTime = match ? match[1] : '5';
        
        const rateLimitError = new Error(`Rate limited: Please wait ${waitTime} minutes before syncing again`);
        rateLimitError.response = error.response;
        throw rateLimitError;
      }
      throw error;
    }
  },

  // Set primary calendar
  setPrimaryCalendar: async (integrationId) => {
    const response = await api.put(`/google-calendar/integrations/${integrationId}/primary`);
    return response.data;
  },

  // Disconnect calendar
  disconnectCalendar: async (integrationId) => {
    const response = await api.delete(`/google-calendar/integrations/${integrationId}`);
    return response.data;
  },

  // List all calendars for user to select.
  // userId: a member's calendars (needs calendar:read_all); omit for your own.
  listCalendars: async (userId) => {
    const response = userId
      ? await api.get('/google-calendar/calendars', { params: { user_id: userId } })
      : await api.get('/google-calendar/calendars');
    return response.data;
  },

  // Save user's calendar selection.
  // userId: a member's selection (needs calendar:select_others); omit for your own.
  selectCalendars: async (calendarIds, userId) => {
    const body = { calendar_ids: calendarIds };
    const response = userId
      ? await api.post('/google-calendar/calendars/select', body, { params: { user_id: userId } })
      : await api.post('/google-calendar/calendars/select', body);
    return response.data;
  },

  // Get user analytics (current user or specific user)
  getUserAnalytics: async (userId = null, params = {}) => {
    const endpoint = userId 
      ? `/google-calendar/analytics/user/${userId}`
      : '/google-calendar/analytics/user';
    
    const response = await api.get(endpoint, { params });
    return response.data;
  },

  // Admin: Get all integrations
  getAllIntegrations: async (skip = 0, limit = 100) => {
    const response = await api.get('/google-calendar/admin/all-integrations', {
      params: { skip, limit: Math.min(limit, 100) }
    });
    return response.data;
  },

  // Admin: Get specific user's integrations
  getUserIntegrations: async (userId) => {
    const response = await api.get(`/google-calendar/admin/users/${userId}/integrations`);
    return response.data;
  },

  // Admin: Get company analytics
  getCompanyAnalytics: async () => {
    const response = await api.get('/google-calendar/analytics/company');
    return response.data;
  },

  // Admin: Get sync logs (paginated response: { data, total, page, page_size, pages })
  getSyncLogs: async (params = {}) => {
    const queryParams = {
      status: params.status && params.status !== 'all' ? params.status : undefined,
      user_id: params.user_id || undefined,
      search: params.search || undefined,
      days: params.days || 7,
      page: params.page || 1,
      page_size: params.page_size || 20,
      sort: params.sort || 'desc'
    };

    const cleanedParams = cleanQueryParams(queryParams);
    const response = await api.get('/google-calendar/admin/sync-logs', {
      params: cleanedParams
    });
    return response.data;
  }
};

// Workspace FAQ API
export const workspaceFAQAPI = {
  // Create a new FAQ
  createFAQ: async (data) => {
    // Validate required fields
    if (!data.keywords?.length) {
      throw new Error('At least one keyword is required');
    }
    if (!data.question?.trim()) {
      throw new Error('Question is required');
    }
    if (!data.answer?.trim()) {
      throw new Error('Answer is required');
    }

    const response = await api.post('/workspace-faqs/text', data);
    return response.data;
  },

  // Get all FAQs with optional filters
  listFAQs: async (params = {}) => {
    // Direct API call - backend workspace-faqs endpoint already filters for FAQs only
    // Supports: search, category, status, location_id, location_ids, sort_by, sort_order, skip, limit
    const response = await api.get('/workspace-faqs/', { params });
    return response.data;
  },

  // Get single FAQ by ID
  getFAQ: async (faqId) => {
    const response = await api.get(`/workspace-faqs/${faqId}/text`);
    return response.data;
  },

  // Update existing FAQ
  updateFAQ: async (faqId, data) => {
    const response = await api.put(`/workspace-faqs/${faqId}/text`, data);
    return response.data;
  },

  // Delete FAQ (soft delete)
  deleteFAQ: async (faqId) => {
    const response = await api.delete(`/workspace-faqs/${faqId}`);
    return response.data;
  },

  // Bulk import FAQs
  bulkImportFAQs: async (faqs) => {
    const response = await api.post('/workspace-faqs/bulk-import', faqs);
    return response.data;
  },

  // Validate FAQ data
  validateFAQ: (data) => {
    const errors = [];

    if (!data.keywords?.length) {
      errors.push('At least one keyword is required');
    }

    if (!data.question?.trim()) {
      errors.push('Question is required');
    }

    if (!data.answer?.trim()) {
      errors.push('Answer is required');
    }

    if (data.question && data.question.length < 5) {
      errors.push('Question must be at least 5 characters');
    }

    if (data.answer && data.answer.length < 10) {
      errors.push('Answer must be at least 10 characters');
    }

    return errors;
  },

  // Polish FAQ text with AI (Kiln-ify!)
  polishText: async (text, textType) => {
    const response = await api.post('/workspace-faqs/polish', {
      text,
      text_type: textType
    });
    return response.data;
  },

  // Extract / refine keywords with AI (Kiln-ify! for keywords).
  // Always sends question + answer. When `existingKeywords` is non-empty,
  // the backend treats them as seeds and returns a refined + expanded list.
  extractKeywords: async (question, answer, existingKeywords) => {
    const payload = { question, answer };
    if (Array.isArray(existingKeywords) && existingKeywords.length > 0) {
      payload.existing_keywords = existingKeywords;
    }
    const response = await api.post('/workspace-faqs/extract-keywords', payload);
    return response.data;
  },

  // Upload an image embedded in a FAQ answer (rich text editor).
  // Uses the shared `api` instance so the host comes from the runtime-resolved
  // baseURL (window.RUNTIME_CONFIG.API_URL) instead of a build-time constant.
  uploadImage: async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/workspace-faqs/upload-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  // Upload a file to show UNDER the FAQ answer (image / video / PDF / PPT / DOC).
  // Generic endpoint — `owner_type` tells the backend which permission applies.
  // Returns {success, type, url, filename, size, mime_type}; the FAQ form sends
  // that object back in `attachments` on create/update.
  uploadAttachment: async (file, onUploadProgress) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/attachments/upload', formData, {
      params: { owner_type: 'faq' },
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
      onUploadProgress: onUploadProgress ? (progressEvent) => {
        const total = progressEvent.total || progressEvent.loaded || 1;
        onUploadProgress(Math.round((progressEvent.loaded * 100) / total));
      } : undefined
    });
    return response.data;
  }
};

// ==================== BULK SMS API (NEW) ====================
export const bulkSMSAPI = {
  /**
   * Preview recipients before sending (no SMS sent)
   */
  preview: async (data) => {
    const response = await api.post('/admin/bulk-sms/preview', data);
    return response.data;
  },

  /**
   * Send bulk SMS to recipients
   */
  send: async (data) => {
    const response = await api.post('/admin/bulk-sms/send', data);
    return response.data;
  },

  /**
   * Get broadcast history (paginated)
   */
  getHistory: async (params = {}) => {
    const response = await api.get('/admin/bulk-sms/history', { params });
    return response.data;
  },

  /**
   * Get broadcast details with recipients
   */
  getDetails: async (broadcastId) => {
    const response = await api.get(`/admin/bulk-sms/${broadcastId}`);
    return response.data;
  },

  /**
   * Get location options for dropdown
   */
  getLocationOptions: async () => {
    const response = await api.get('/admin/bulk-sms/locations/options');
    return response.data;
  },

  /**
   * Get member options for dropdown (with search)
   */
  getMemberOptions: async (search = '') => {
    const response = await api.get('/admin/bulk-sms/members/options', {
      params: { search }
    });
    return response.data;
  },

  /**
   * Resend to failed recipients only
   */
  resendFailed: async (broadcastId, maxRetries = 3) => {
    const response = await api.post(`/admin/bulk-sms/${broadcastId}/resend-failed`, {
      max_retries: maxRetries
    });
    return response.data;
  },

  /**
   * Resend to ALL original recipients
   */
  resendAll: async (broadcastId) => {
    const response = await api.post(`/admin/bulk-sms/${broadcastId}/resend-all`);
    return response.data;
  }
};

/**
 * Live Conversations API - Admin monitoring and interaction with user conversations
 * Phase 1: View Only (list conversations, view messages, stats)
 */
export const liveConversationsAPI = {
  /**
   * List active conversations with filters
   * @param {Object} params - Query parameters
   * @param {number} params.limit - Max conversations to return (1-100, default 50)
   * @param {number} params.offset - Pagination offset (default 0)
   * @param {string} params.search - Search by name, email, phone
   * @param {string} params.location_id - Filter by location (OfficeRND ID)
   * @param {string} params.status - Filter: active, all, resolved, escalated
   * @param {number} params.active_within_minutes - Time window for active (default 15, max 1440)
   * @param {string} params.sort_by - Sort by: last_active, created_at, message_count
   * @param {string} params.order - Order: asc or desc
   * @returns {Promise} - {conversations, total, active_count, resolved_count, poll_interval_ms}
   */
  listConversations: async (params = {}, config = {}) => {
    const response = await api.get('/admin/conversations/live', { params, ...config });
    return response.data;
  },

  /**
   * Get messages for a specific conversation
   * @param {string} sessionId - Chat session ID
   * @param {Object} params - Query parameters
   * @param {number} params.limit - Max messages (1-500, default 100)
   * @param {number} params.offset - Pagination offset
   * @param {string} params.since_timestamp - ISO datetime for polling (get only new messages)
   * @param {boolean} params.include_metadata - Include message metadata (default true)
   * @returns {Promise} - {conversation, messages, has_more, total, latest_timestamp}
   */
  getMessages: async (sessionId, params = {}, config = {}) => {
    const response = await api.get(`/admin/conversations/${sessionId}/messages`, { params, ...config });
    return response.data;
  },

  /**
   * Send admin reply to a conversation (Phase 2)
   * Sends a JSON body. The backend adds the "{First} from {Company}:" attribution
   * prefix itself — never include it here.
   * @param {string} sessionId - Chat session ID
   * @param {string} message - Message content (1-5000 chars, no prefix)
   * @param {boolean|null} [alsoSendSms] - Per-reply SMS override. Pass true/false only
   *   if the admin TOUCHED the "Also send by SMS" checkbox; omit/undefined/null so the
   *   backend follows the live_conversation_reply.sms_delivery_enabled config.
   * @param {boolean} [sendAsTeam] - When true, the backend attributes this reply to
   *   "From {Company} Concierge Team:" instead of the admin's own name.
   * @returns {Promise} - {success, message, delivery}
   */
  sendReply: async (sessionId, message, alsoSendSms, sendAsTeam) => {
    const body = { message };
    if (alsoSendSms !== undefined && alsoSendSms !== null) {
      body.also_send_sms = alsoSendSms;
    }
    if (sendAsTeam) {
      body.send_as_team = true;
    }
    const response = await api.post(`/admin/conversations/${sessionId}/reply`, body);
    return response.data;
  },

  /**
   * Toggle AI on/off for a conversation (Phase 2)
   * @param {string} sessionId - Chat session ID
   * @param {boolean} enabled - true to enable AI, false to disable
   * @returns {Promise} - {success, session_id, ai_enabled, message}
   */
  toggleAI: async (sessionId, enabled) => {
    const response = await api.patch(`/admin/conversations/${sessionId}/ai-toggle`, null, {
      params: { enabled }
    });
    return response.data;
  },

  /**
   * Get conversation statistics
   * @param {Object} params - Query parameters
   * @param {number} params.active_within_minutes - Time window for active count (default 15)
   * @returns {Promise} - {active_count, resolved_count, escalated_count, total_count}
   */
  getStats: async (params = {}) => {
    const response = await api.get('/admin/conversations/stats', { params });
    return response.data;
  },

  /**
   * Submit admin feedback on a message
   * @param {string} sessionId - Chat session ID
   * @param {number} messageId - Message ID
   * @param {string} rating - 'thumbs_up' or 'thumbs_down'
   * @param {string} feedbackText - Optional feedback note
   * @returns {Promise} - {success, feedback}
   */
  submitFeedback: async (sessionId, messageId, rating, feedbackText = '') => {
    const params = { rating };
    if (feedbackText) params.feedback_text = feedbackText;
    const response = await api.post(
      `/admin/conversations/${sessionId}/messages/${messageId}/feedback`,
      null,
      { params }
    );
    return response.data;
  },

  /**
   * Get feedback for a single message
   * @param {string} sessionId - Chat session ID
   * @param {number} messageId - Message ID
   * @returns {Promise} - {message_id, feedback_entries}
   */
  getMessageFeedback: async (sessionId, messageId) => {
    const response = await api.get(
      `/admin/conversations/${sessionId}/messages/${messageId}/feedback`
    );
    return response.data;
  },

  /**
   * Get batch feedback summary for all messages in a conversation
   * @param {string} sessionId - Chat session ID
   * @returns {Promise} - {session_id, message_feedback_map}
   */
  getFeedbackSummary: async (sessionId) => {
    const response = await api.get(`/admin/conversations/${sessionId}/feedback-summary`);
    return response.data;
  },

  /**
   * Get available feedback statuses for dropdown.
   * Call once on page load and cache — values don't change at runtime.
   * @returns {Promise<{statuses: Array<{value: string, label: string, color: string}>}>}
   */
  getFeedbackStatuses: async () => {
    const response = await api.get('/admin/conversations/feedback-statuses');
    return response.data;
  },

  /**
   * Get full feedback detail with activity timeline.
   * @param {string} sessionId - Conversation session ID
   * @param {number} feedbackId - Feedback ID from feedbacks[] array
   * @returns {Promise<Object>} - Detail with timeline[] array
   */
  getFeedbackDetail: async (sessionId, feedbackId) => {
    const response = await api.get(
      `/admin/conversations/${sessionId}/feedback/${feedbackId}`
    );
    return response.data;
  },

  /**
   * Change feedback status.
   * After success, caller should refresh feedback-summary to update badges.
   * @param {string} sessionId - Conversation session ID
   * @param {number} feedbackId - Feedback ID
   * @param {string} status - New status value from /feedback-statuses endpoint
   * @returns {Promise<{success: boolean, id: number, status: string, updated_at: string}>}
   */
  updateFeedbackStatus: async (sessionId, feedbackId, status) => {
    const response = await api.patch(
      `/admin/conversations/${sessionId}/feedback/${feedbackId}/status`,
      { status }
    );
    return response.data;
  },

  /**
   * Add comment to feedback with optional status change.
   * After success, caller should refresh feedback-summary and re-fetch detail.
   * @param {string} sessionId - Conversation session ID
   * @param {number} feedbackId - Feedback ID
   * @param {string} commentText - Comment content (1-10000 chars)
   * @param {string|null} newStatus - Optional new status to set alongside comment
   * @returns {Promise<Object>} - New comment/timeline entry
   */
  addFeedbackComment: async (sessionId, feedbackId, commentText, newStatus = null) => {
    const body = { comment_text: commentText };
    if (newStatus) body.new_status = newStatus;
    const response = await api.post(
      `/admin/conversations/${sessionId}/feedback/${feedbackId}/comments`,
      body
    );
    return response.data;
  }
};

/**
 * Message Feedback API - Admin feedback management
 * Admin can view and analyze user feedback on AI messages
 */
export const feedbackAPI = {
  /**
   * List all feedback with pagination and filtering
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (1-indexed, default 1)
   * @param {number} params.limit - Items per page (1-100, default 50)
   * @param {string} params.rating - Filter by rating: 'thumbs_up' or 'thumbs_down'
   * @returns {Promise} - {total, page, limit, pages, data}
   */
  getList: async (params = {}) => {
    const response = await api.get('/admin/feedback', { params });
    const result = response.data;

    // Log the raw response shape for debugging (sanitized + dev-only)
    if (result.data && result.data.length > 0) {
      logger.debug('FEEDBACK', 'Raw backend response (first item)', {
        firstItem: result.data[0],
      });
    }

    // Normalize field names for each item
    if (result.data && Array.isArray(result.data)) {
      result.data = result.data.map(item => {
        // Calculate notes count from timeline if available
        let notesCount = 0;

        // Check if backend provides timeline in list response
        if (item.timeline && Array.isArray(item.timeline)) {
          // Count timeline entries that are notes (no status change)
          notesCount = item.timeline.filter(
            entry => entry.old_status === null && entry.new_status === null
          ).length;
          logger.debug('FEEDBACK', `Feedback #${item.id}: timeline-derived notes`, {
            id: item.id,
            timelineEntries: item.timeline.length,
            notesCount,
          });
        } else {
          // Backend doesn't include timeline in list response
          // Check if it provides notes_count directly
          notesCount = item.notes_count || item.comments_count || 0;
          logger.debug('FEEDBACK', `Feedback #${item.id}: notes_count fallback`, {
            id: item.id,
            notesCount,
          });
        }

        return {
          ...item,
          // Normalize notes field (backend may use different name)
          notes: item.notes || item.comments || [],
          // Use calculated/provided count
          notes_count: notesCount
        };
      });
    }

    return result;
  },

  /**
   * Get single feedback with full conversation context
   * @param {number} feedbackId - Feedback record ID
   * @returns {Promise} - Complete feedback record with conversation_snapshot
   */
  getDetail: async (feedbackId) => {
    const response = await api.get(`/admin/feedback/${feedbackId}`);
    const data = response.data;

    // Backend returns 'timeline' array instead of separate 'notes' and 'activity_history'
    // Timeline contains both status changes and admin notes
    if (data.timeline && Array.isArray(data.timeline)) {
      // Split timeline into notes and activity_history
      data.notes = [];
      data.activity_history = [];

      data.timeline.forEach(item => {
        // Normalize field name
        const normalizedItem = {
          ...item,
          note_text: item.comment_text || item.note_text,
          admin_name: item.admin ? `${item.admin.first_name} ${item.admin.last_name}` : 'Admin'
        };

        // If has status change (old_status/new_status), it's an activity
        if (item.old_status !== null && item.new_status !== null) {
          data.activity_history.push({
            ...normalizedItem,
            event_type: 'status_changed',
            from_status: item.old_status,
            to_status: item.new_status,
            timestamp: item.created_at
          });
        }
        // If no status change, it's a note
        else {
          data.notes.push(normalizedItem);
        }
      });
    }

    // Fallback: Normalize field names in notes array if backend uses different structure
    if (data.notes && Array.isArray(data.notes)) {
      data.notes = data.notes.map(note => ({
        ...note,
        note_text: note.note_text || note.comment_text,
        admin_name: note.admin ? `${note.admin.first_name} ${note.admin.last_name}` : note.admin_name || 'Admin'
      }));
    }

    return data;
  },

  /**
   * Submit feedback on a message (User endpoint)
   * @param {number} messageId - Message ID to provide feedback on
   * @param {Object} data - Feedback data
   * @param {string} data.rating - 'thumbs_up' or 'thumbs_down'
   * @param {string} data.feedback_text - Optional user comment
   * @returns {Promise} - Created feedback record
   */
  submitFeedback: async (messageId, data) => {
    const response = await api.post(`/messages/${messageId}/feedback`, data);
    return response.data;
  },

  /**
   * Get current user's feedback map (User endpoint)
   * Returns which messages the user has already provided feedback for
   * @returns {Promise<Object>} - Map of {messageId: rating}
   * Example: {"123": "thumbs_up", "456": "thumbs_down"}
   */
  getMyFeedback: async () => {
    const response = await api.get('/feedback/my-feedback');
    return response.data;
  },

  /**
   * Get feedback statistics
   * @returns {Promise} - {total, thumbs_up_count, thumbs_down_count, satisfaction_rate}
   */
  getStats: async () => {
    try {
      // Try to get stats from dedicated endpoint first
      const statsResponse = await api.get('/admin/feedback/stats');
      return statsResponse.data;
    } catch (statsError) {
      // Fallback: calculate from list data
      try {
        const response = await api.get('/admin/feedback', { params: { limit: 1000 } });

        // Handle different possible response structures
        let data = [];

        if (Array.isArray(response.data)) {
          data = response.data;
        } else if (response.data && Array.isArray(response.data.data)) {
          data = response.data.data;
        } else if (response.data && Array.isArray(response.data.items)) {
          data = response.data.items;
        }

        const thumbsUp = data.filter(f => f.rating === 'thumbs_up').length;
        const thumbsDown = data.filter(f => f.rating === 'thumbs_down').length;
        const total = data.length;
        const satisfactionRate = total > 0 ? parseFloat(((thumbsUp / total) * 100).toFixed(1)) : 0;

        const stats = {
          total,
          thumbs_up_count: thumbsUp,
          thumbs_down_count: thumbsDown,
          satisfaction_rate: satisfactionRate
        };

        return stats;
      } catch (error) {

logger.error('API', '[feedbackAPI] Failed to calculate stats:', { error: error.message });
        throw error;
      }
    }
  },

  /**
   * Update feedback status (Admin only)
   * @param {number} feedbackId - Feedback record ID
   * @param {string} status - New status value
   * @returns {Promise} - Updated feedback record
   *
   * Statuses:
   * - new: 🔴 Just submitted
   * - working: 🟡 Admin started working
   * - in_progress: 🔵 Investigation ongoing
   * - resolved: ✅ Issue fixed
   * - wont_fix: 🚫 Won't be fixed
   * - not_found: ❓ Cannot find issue
   * - cannot_reproduce: ⚠️ Cannot reproduce
   * - closed: Closed without action
   */
  updateStatus: async (feedbackId, status) => {
    const response = await api.patch(`/admin/feedback/${feedbackId}/status`, { status });
    return response.data;
  },

  /**
   * Add admin note to feedback (Admin only)
   * @param {number} feedbackId - Feedback record ID
   * @param {string} noteText - Note content (max 2000 characters)
   * @returns {Promise} - Created note record
   */
  addNote: async (feedbackId, noteText) => {
    const response = await api.post(`/admin/feedback/${feedbackId}/notes`, { comment_text: noteText });
    return response.data;
  }
};

/**
 * Error Notifications API - Admin error tracking
 *
 * Mirrors feedbackAPI by design: the Errors tab is the Message Feedback tab cloned
 * with the base path swapped (/admin/feedback -> /admin/errors). Same auth, same
 * status chips, same comment thread. See backend ERROR_NOTIFICATION_FRONTEND_CONTRACT.md.
 *
 * Permissions: GET requires feedback:read, PATCH/POST require feedback:write.
 */
export const errorAPI = {
  /**
   * List errors with pagination + filters.
   * Backend returns { items, total, page, limit, total_pages }; we normalize to the
   * { data, total, page, limit, pages } shape the page consumes (same as feedback).
   * @param {Object} params - page, limit, type, status, severity, user_id, request_id, date_start, date_end
   */
  getList: async (params = {}) => {
    const response = await api.get('/admin/errors', { params });
    const result = response.data || {};
    return {
      data: result.items || [],
      total: result.total || 0,
      page: result.page || params.page || 1,
      limit: result.limit || params.limit || 50,
      pages: result.total_pages || 0
    };
  },

  /**
   * Get a single error with full context (traceback, flow trail, logs).
   * @param {number} errorId
   */
  getDetail: async (errorId) => {
    const response = await api.get(`/admin/errors/${errorId}`);
    return response.data;
  },

  /**
   * Get the comment thread for an error (oldest first). Status changes are
   * auto-recorded as comments with old_status/new_status set.
   * @param {number} errorId
   */
  getComments: async (errorId) => {
    const response = await api.get(`/admin/errors/${errorId}/comments`);
    return response.data;
  },

  /**
   * Change error status. The change is auto-recorded as a comment by the backend.
   * @param {number} errorId
   * @param {string} status - value from feedbackConstants (shared status palette)
   */
  updateStatus: async (errorId, status) => {
    const response = await api.patch(`/admin/errors/${errorId}/status`, { status });
    return response.data;
  },

  /**
   * Add a comment, optionally also changing status.
   * @param {number} errorId
   * @param {string} commentText
   * @param {string|null} newStatus - optional status change
   */
  addComment: async (errorId, commentText, newStatus = null) => {
    const body = { comment_text: commentText };
    if (newStatus) body.new_status = newStatus;
    const response = await api.post(`/admin/errors/${errorId}/comments`, body);
    return response.data;
  },

  /**
   * Split the comment thread into { notes, activity_history } so the shared
   * ActivityTimeline component (from feedback) can render it unchanged.
   * Rows with old_status/new_status set are status-change audit entries.
   * @param {Array} comments
   */
  splitComments: (comments = []) => {
    const notes = [];
    const activity_history = [];
    comments.forEach(c => {
      const adminName = c.admin
        ? `${c.admin.first_name || ''} ${c.admin.last_name || ''}`.trim() || 'Admin'
        : 'Admin';
      if (c.old_status !== null && c.old_status !== undefined &&
          c.new_status !== null && c.new_status !== undefined) {
        activity_history.push({
          ...c,
          event_type: 'status_changed',
          from_status: c.old_status,
          to_status: c.new_status,
          admin_name: adminName,
          timestamp: c.created_at
        });
      } else {
        notes.push({
          ...c,
          note_text: c.comment_text || c.note_text,
          admin_name: adminName,
          timestamp: c.created_at
        });
      }
    });
    return { notes, activity_history };
  },

  /**
   * Fire a test Telegram alert (settings page button). No body.
   * @returns {Promise} - { ready, sent, errors, reasons, chat_ids_count }
   */
  sendTestAlert: async () => {
    const response = await api.post('/admin/errors/test-alert');
    return response.data;
  }
};

// ==================== APP SETTINGS API ====================
/**
 * Dynamic App Settings (admin). The backend is the single source of truth —
 * the frontend renders whatever these endpoints return. See
 * docs/plans/DYNAMIC_SETTINGS_FRONTEND_IMPLEMENTATION_PLAN.md
 *
 * Permissions: GET requires settings:read, PUT/POST require settings:write.
 */
export const settingsAPI = {
  // GET /admin/settings/groups → [{ group, categories: [{ category, label, description, has_saved_row }] }]
  getSettingGroups: async () => {
    logger.debug('SETTINGS', 'Fetching setting groups');
    try {
      const response = await api.get('/admin/settings/groups');
      return response.data;
    } catch (error) {
      logger.error('SETTINGS', 'Failed to fetch setting groups', { error: error.message });
      throw error;
    }
  },

  // GET /admin/settings/{category} → full field schema for one card
  getSettings: async (category) => {
    logger.debug('SETTINGS', 'Fetching settings', { category });
    try {
      const response = await api.get(`/admin/settings/${category}`);
      return response.data;
    } catch (error) {
      logger.error('SETTINGS', 'Failed to fetch settings', { category, error: error.message });
      throw error;
    }
  },

  // PUT /admin/settings/{category} with body { value: { KEY: val, ... } } → refreshed schema
  updateSettings: async (category, value) => {
    logger.debug('SETTINGS', 'Updating settings', { category });
    try {
      const response = await api.put(`/admin/settings/${category}`, { value });
      logger.success('SETTINGS', 'Settings updated', { category });
      return response.data;
    } catch (error) {
      logger.error('SETTINGS', 'Failed to update settings', { category, error: error.message });
      throw error;
    }
  },

  // GET /admin/settings/balance-alert/member-options → recipients picker users
  getBalanceAlertMemberOptions: async (search = '', ids = null) => {
    logger.debug('SETTINGS', 'Fetching balance-alert member options', { search, ids });
    try {
      const params = { search };
      if (Array.isArray(ids) && ids.length) params.ids = ids.join(',');
      const response = await api.get('/admin/settings/balance-alert/member-options', {
        params,
      });
      return response.data;
    } catch (error) {
      logger.error('SETTINGS', 'Failed to fetch balance-alert member options', { error: error.message });
      throw error;
    }
  },

  // POST /admin/settings/{category}/reset → clears overrides, reverts to env/default
  resetSettings: async (category) => {
    logger.debug('SETTINGS', 'Resetting settings', { category });
    try {
      const response = await api.post(`/admin/settings/${category}/reset`);
      logger.success('SETTINGS', 'Settings reset', { category });
      return response.data;
    } catch (error) {
      logger.error('SETTINGS', 'Failed to reset settings', { category, error: error.message });
      throw error;
    }
  },

  // POST /admin/settings/sync → idempotent one-time setup (creates missing rows)
  syncSettings: async () => {
    logger.debug('SETTINGS', 'Syncing settings');
    try {
      const response = await api.post('/admin/settings/sync');
      logger.success('SETTINGS', 'Settings synced');
      return response.data;
    } catch (error) {
      logger.error('SETTINGS', 'Failed to sync settings', { error: error.message });
      throw error;
    }
  }
};

// ==================== PARTNER INTEGRATIONS API (read-only) ====================
/**
 * Who we send webhooks to and who may call our partner API — names, hosts,
 * events/scopes, key counts. Never a key or a URL path. There is no write call:
 * integrations are changed only in server config.
 * Permission: settings:read. See docs/plans/PARTNER_INTEGRATIONS_ADMIN_VIEW_PLAN.md (backend).
 */
export const integrationsAPI = {
  // GET /admin/integrations/partners → { outgoing: {enabled, config_ok, partners}, incoming: {config_ok, partners} }
  getPartners: async () => {
    logger.debug('INTEGRATIONS', 'Fetching partner integrations');
    try {
      const response = await api.get('/admin/integrations/partners');
      return response.data;
    } catch (error) {
      logger.error('INTEGRATIONS', 'Failed to fetch partner integrations', { error: error.message });
      throw error;
    }
  },
};

// ==================== ATTACHMENT API ====================
/**
 * Upload file for support ticket attachments
 * Uses general /api/upload endpoint (no admin permission required)
 *
 * @param {File} file - File to upload (image or video)
 * @param {Function} onUploadProgress - Optional progress callback
 * @returns {Promise<Object>} - {success, url, filename, size}
 */
// ==================== LOGS API ====================
/**
 * Admin Logs API - Database-persistent logs (system, chat flow, user debug, cleanup)
 * Requires system:read permission
 */
export const logsAPI = {
  /** System logs from backend (errors, warnings, info) */
  getBackendLogs: async (params = {}) => {
    const response = await api.get('/admin/logs/backend', { params });
    return response.data;
  },

  /** Frontend browser logs (errors from user's browser) */
  getFrontendLogs: async (params = {}) => {
    const response = await api.get('/admin/logs/frontend', { params });
    return response.data;
  },

  /** Chat processing flow logs grouped by request_id */
  getChatFlowLogs: async (params = {}) => {
    const response = await api.get('/admin/logs/chat-flow', { params });
    return response.data;
  },

  /** Full debug view for one user (messages + flows + errors) */
  getUserLogs: async (userId, params = {}) => {
    const response = await api.get(`/admin/logs/user/${userId}`, { params });
    return response.data;
  },

  /** Full trace for a single request (end-to-end) */
  getRequestTrace: async (requestId) => {
    const response = await api.get(`/admin/logs/request/${requestId}`);
    return response.data;
  },

  /** Feedback investigation trace (thumbs-down debugging) */
  getFeedbackTrace: async (feedbackId) => {
    const response = await api.get(`/admin/logs/feedback/${feedbackId}/trace`);
    return response.data;
  },

  /** Delete old logs for storage management (starts a background job) */
  cleanupLogs: async (params = {}) => {
    const response = await api.delete('/admin/logs/cleanup', { params });
    return response.data;
  },

  /** Progress of the latest log cleanup: idle | running | done | failed | interrupted */
  getCleanupStatus: async () => {
    const response = await api.get('/admin/logs/cleanup/status');
    return response.data;
  }
};

// ==================== ATTACHMENT API ====================
/**
 * Upload file for support ticket attachments
 * Uses general /api/upload endpoint (no admin permission required)
 *
 * @param {File} file - File to upload (image or video)
 * @param {Function} onUploadProgress - Optional progress callback
 * @returns {Promise<Object>} - {success, url, filename, size}
 */
export const uploadAttachment = async (file, onUploadProgress) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post('/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 120000, // 2 minutes for file uploads
    onUploadProgress: onUploadProgress ? (progressEvent) => {
      const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
      onUploadProgress(percentCompleted, progressEvent);
    } : undefined
  });

  return response.data;
};

export default api;
